import { Box } from '@mui/material';
import { PLATFORM_SLA_DISPLAY_ORDER } from '@provision/service-level-utils';
import { useMemo, useState } from 'react';
import { COPY } from '../../copy';
import { resolveCta } from '../../cta';
import { PageSurface } from '../../layouts/block-surface';
import { useLayoutStyles } from '../../layouts/layout-context';
import type { BlockRenderProps, PlanVariant } from '../../layouts/types';
import { RefreshIcon, StarIcon } from '../../primitives/icons';
import { SectionHead } from '../../primitives/section-head';
import { Wrap } from '../../primitives/wrap';
import type { LandingPageFeature, LandingPageProduct } from '../../types';
import { BillingToggle } from './billing-toggle';
import { PlanCard } from './plan-card';
import { rollUpRows } from './plan-rows';

export type BillingPeriod = 'monthly' | 'annual';

const NO_PRODUCTS: LandingPageProduct[] = [];
const NO_FEATURES: LandingPageFeature[] = [];
const SIGNATURE_SLA_ID: string | undefined = PLATFORM_SLA_DISPLAY_ORDER.at(-1);

function planVariant(product: LandingPageProduct, mostPopularProductId?: string): PlanVariant {
  if (product.platformSlaId && product.platformSlaId === SIGNATURE_SLA_ID) return 'signature';
  return product.id === mostPopularProductId ? 'featured' : 'default';
}

export const PlansBlock = ({
  block,
  plans,
  settings,
  savingsPercent,
  onCta,
  pendingCta,
}: BlockRenderProps) => {
  const s = useLayoutStyles('plans');
  const shared = useLayoutStyles('shared');
  const [chosenPeriod, setChosenPeriod] = useState<BillingPeriod>('monthly');

  const products = plans.products ?? NO_PRODUCTS;
  const features = plans.features ?? NO_FEATURES;
  const rows = useMemo(() => rollUpRows(products, features), [products, features]);

  // `!== false`: a flag unset on an older config must not produce a priceless grid.
  const periods = (['monthly', 'annual'] as const).filter((p) =>
    p === 'monthly' ? plans.showMonthlyPrices !== false : plans.showAnnualPrices !== false,
  );
  const period = periods.includes(chosenPeriod) ? chosenPeriod : (periods[0] ?? 'monthly');

  if (block.type !== 'plans' || products.length === 0) return null;

  return (
    <Box sx={s.section}>
      <Wrap>
        <SectionHead
          kicker={block.kicker?.trim() || COPY.plansKicker}
          icon={<StarIcon sx={shared.kickerIcon} />}
          title={block.title?.trim() || COPY.plansHeadline(products.length)}
          lede={block.subtext?.trim() || COPY.plansLede}
        />
        <PageSurface>
          {settings.showPricing && periods.length === 2 ? (
            <BillingToggle
              period={period}
              onChange={setChosenPeriod}
              savingsPercent={savingsPercent}
            />
          ) : null}
          <Box sx={s.grid}>
            {products.map((product, index) => {
              // A plan with no price for this period has nothing to check out either.
              const callForPricing =
                Boolean(product.callForPricing) || !product.prices[period];
              return (
                <PlanCard
                  key={product.id}
                  product={product}
                  rows={rows[index]}
                  variant={planVariant(product, plans.mostPopularProductId)}
                  isMostPopular={product.id === plans.mostPopularProductId}
                  period={period}
                  showPricing={settings.showPricing}
                  savingsPercent={savingsPercent}
                  callForPricing={callForPricing}
                  callForPricingPhone={plans.callForPricingPhone}
                  cta={resolveCta({
                    settings,
                    onCta,
                    pendingCta,
                    blockId: block.id,
                    pricePointId: product.prices[period]?.id,
                    callForPricing,
                  })}
                />
              );
            })}
          </Box>
        </PageSurface>
        <Box sx={s.foot}>
          <RefreshIcon sx={s.footIcon} />
          {COPY.plansFoot}
        </Box>
      </Wrap>
    </Box>
  );
};
